import React, { useState, useRef, useEffect } from 'react';
import { Eye, ArrowRight, LogIn, Link, Building2, Shield, Share2, FileText, Users, BarChart2, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const RealEstate = () => {
  const [activeStatIndex, setActiveStatIndex] = useState(0);
  const statsRef = useRef(null);
  const navigate = useNavigate();

  const handleDemo = () => {
    navigate('/demo-preview-realestate');
  };

  const handleSignUp = () => {
    navigate('/signup');
  };

  const handleSignIn = () => {
    navigate('/signin');
  };

  const handleStatsScroll = () => {
    if (statsRef.current) {
      const scrollLeft = statsRef.current.scrollLeft;
      const cardWidth = statsRef.current.offsetWidth * 0.8;
      const newIndex = Math.round(scrollLeft / cardWidth);
      setActiveStatIndex(newIndex);
    }
  };

  useEffect(() => {
    const statsContainer = statsRef.current;
    statsContainer?.addEventListener('scroll', handleStatsScroll);
    return () => {
      statsContainer?.removeEventListener('scroll', handleStatsScroll);
    };
  }, []);

  const renderDotIndicator = (count, activeIndex) => (
    <div className="flex justify-center gap-2 mt-6 sm:hidden mb-16">
      {[...Array(count)].map((_, index) => (
        <div
          key={index}
          className={`w-2 h-2 rounded-full transition-all duration-300 ${
            index === activeIndex ? 'bg-blue-400 w-4' : 'bg-slate-600'
          }`}
        />
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-900 text-white flex flex-col">
      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="text-center max-w-4xl mx-auto w-full">
          {/* Glow Effect */}
          <div className="absolute -z-10 w-64 h-64 sm:w-96 sm:h-96 bg-blue-500/20 rounded-full blur-3xl"></div>
          
          <h1 className="text-3xl sm:text-5xl md:text-7xl lg:text-8xl font-light tracking-tight mb-6 sm:mb-8 px-2">
            Secure Your Real Estate Deals with<br />
            <span className="text-blue-400">AI-Powered Virtual Deal Rooms</span>
          </h1>
          
          <p className="text-lg sm:text-xl md:text-2xl text-slate-300 mb-6 sm:mb-8 font-light max-w-3xl mx-auto leading-relaxed px-2">
            Enhance security, streamline collaboration, and gain insights with the leading AI-first virtual deal room for real estate professionals.
          </p>

          {/* Link Preview */}
          <div className="p-4 max-w-sm mx-auto">
            <div className="bg-slate-900/80 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4 hover:border-blue-500/30 transition-all duration-300">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-violet-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Link className="w-4 h-4 text-white" />
                </div>
                <div className="font-mono text-sm truncate">
                  <span className="text-blue-400">decklink.co/p/</span>
                  <span className="text-slate-300">yourdealroom</span>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16">
            <button
              onClick={handleDemo}
              className="w-full sm:w-auto px-12 py-6 bg-slate-800 hover:bg-slate-700 border border-slate-600 hover:border-blue-400 text-white hover:text-blue-400 rounded-xl text-xl font-medium transition-all flex items-center justify-center gap-3 group min-w-[280px]"
            >
              <Eye className="w-6 h-6" />
              View Live Demo
            </button>
            
            <button
              onClick={handleSignUp}
              className="w-full sm:w-auto px-12 py-6 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-xl text-xl font-medium transition-all flex items-center justify-center gap-3 group min-w-[280px]"
            >
              Get Started Free
              <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={handleSignIn}
              className="w-full sm:w-auto px-12 py-6 border border-slate-600 hover:border-blue-400 text-slate-300 hover:text-blue-400 bg-transparent rounded-xl text-xl font-medium transition-all flex items-center justify-center gap-3 group min-w-[280px]"
            >
              Sign In
            </button>
          </div>

          {/* Why Choose Section */}
          <div className="mb-16">
            <h2 className="text-2xl sm:text-3xl font-light mb-8">Why Choose Our Virtual Deal Room?</h2>
            <p className="text-slate-300 mb-12 max-w-3xl mx-auto">
              In the fast-paced world of real estate, managing sensitive documents securely and efficiently is crucial. 
              Our AI-powered virtual deal room simplifies property acquisitions, sales, leasing, and investor communications.
            </p>
          </div>

          {/* Features */}
          <div className="p-4">
            <div 
              ref={statsRef}
              className="flex overflow-x-auto snap-x snap-mandatory gap-4 pb-4 scrollbar-hide px-2 -mx-2 sm:px-0 sm:mx-0 sm:justify-center sm:gap-6 mb-4 sm:mb-8"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              <div className="flex-none w-[85vw] max-w-[285px] sm:w-auto sm:max-w-none bg-gradient-to-br from-blue-600/20 to-blue-800/20 backdrop-blur border border-blue-500/30 rounded-2xl p-6 sm:p-8 hover:border-blue-400/50 transition-all group snap-start relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 animate-pulse"></div>
                <div className="relative">
                  <div className="text-3xl sm:text-4xl md:text-5xl font-light text-blue-400 mb-2">
                    <Shield className="w-8 h-8 mb-2" />
                  </div>
                  <div className="text-slate-300 font-medium mb-1 text-sm sm:text-base">Advanced Security</div>
                  <div className="text-xs text-blue-300/70">Bank-grade protection</div>
                  <div className="mt-4 space-y-2 text-sm text-blue-300/70">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-blue-400" />
                      <span>Multi-factor authentication</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-blue-400" />
                      <span>AI-powered detection</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex-none w-[85vw] max-w-[285px] sm:w-auto sm:max-w-none bg-gradient-to-br from-green-600/20 to-emerald-800/20 backdrop-blur border border-green-500/30 rounded-2xl p-6 sm:p-8 hover:border-green-400/50 transition-all group snap-start relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-green-500/5 to-emerald-500/5 animate-pulse" style={{ animationDelay: '0.5s' }}></div>
                <div className="relative">
                  <div className="text-3xl sm:text-4xl md:text-5xl font-light text-green-400 mb-2">
                    <FileText className="w-8 h-8 mb-2" />
                  </div>
                  <div className="text-slate-300 font-medium mb-1 text-sm sm:text-base">AI-Driven Management</div>
                  <div className="text-xs text-green-300/70">Smart organization</div>
                  <div className="mt-4 space-y-2 text-sm text-green-300/70">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span>Automatic organization</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span>Smart document tagging</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex-none w-[85vw] max-w-[285px] sm:w-auto sm:max-w-none bg-gradient-to-br from-purple-600/20 to-violet-800/20 backdrop-blur border border-purple-500/30 rounded-2xl p-6 sm:p-8 hover:border-purple-400/50 transition-all group snap-start relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-violet-500/5 animate-pulse" style={{ animationDelay: '1s' }}></div>
                <div className="relative">
                  <div className="text-3xl sm:text-4xl md:text-5xl font-light text-purple-400 mb-2">
                    <BarChart2 className="w-8 h-8 mb-2" />
                  </div>
                  <div className="text-slate-300 font-medium mb-1 text-sm sm:text-base">Intelligent Analytics</div>
                  <div className="text-xs text-purple-300/70">Real-time insights</div>
                  <div className="mt-4 space-y-2 text-sm text-purple-300/70">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-purple-400" />
                      <span>Engagement tracking</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-purple-400" />
                      <span>Deal success prediction</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Pagination dots for mobile */}
          {renderDotIndicator(3, activeStatIndex)}

          {/* Additional Features */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-16 max-w-4xl mx-auto">
            <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-xl p-6 hover:border-blue-500/30 transition-all">
              <Share2 className="w-8 h-8 text-blue-400 mb-4" />
              <h3 className="text-xl font-medium mb-2">Easy Sharing</h3>
              <p className="text-slate-300">Share your deal room with a single, secure link—perfect for agents, buyers, and investors.</p>
            </div>
            <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-xl p-6 hover:border-blue-500/30 transition-all">
              <Users className="w-8 h-8 text-blue-400 mb-4" />
              <h3 className="text-xl font-medium mb-2">Real-Time Collaboration</h3>
              <p className="text-slate-300">Enable stakeholders to comment and edit documents in real-time for faster deal closures.</p>
            </div>
          </div>

          {/* Simple Social Proof */}
          <p className="text-slate-500 text-sm mt-12 font-light">
            <strong className="text-slate-400">Real estate professionals trust us.</strong> Join the growing number of agents and investors using DeckLink to secure their deals.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RealEstate; 